import { useState, useEffect, useCallback } from 'react';
import {
  Package,
  Plus,
  Search,
  ArrowUpDown,
  AlertTriangle,
  Barcode,
  MapPin,
  Scissors,
  FileDown,
  Settings,
  BarChart3,
  FolderInput,
  ChevronDown,
  Filter,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  StockItem,
  StockMovement,
  MaterialGroup,
  CustomStatus,
  ItemStatus,
  StorageSection,
  StorageEntradasView,
  StorageRelatoriosView,
  StorageConfigView,
} from '../../types';
import {
  fetchStockItems,
  fetchStockMovements,
  fetchMaterialGroups,
  fetchCustomStatuses
} from '../../services/storage';
import { cn } from '../../lib/utils';
import { CreateItemDialog, RecordMovementDialog, ItemDetailDialog, CreateGroupDialog, ScrapGenerationDialog } from '../storage';
import { useToast } from '../../contexts/ToastContext';

interface StoragePageProps {
  accessToken: string;
  initialSection?: StorageSection;
}

const STATUS_COLORS: Record<ItemStatus, string> = {
  'DISPONIVEL': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  'RESERVADO': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  'CONSUMIDO': 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
  'DESCARTE': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

const STATUS_LABELS: Record<ItemStatus, string> = {
  'DISPONIVEL': 'Disponível',
  'RESERVADO': 'Reservado',
  'CONSUMIDO': 'Consumido',
  'DESCARTE': 'Descarte',
};

const ITEM_TYPE_LABELS: Record<string, string> = {
  'MATERIA_PRIMA': 'Matéria-prima',
  'FERRAMENTA': 'Ferramenta',
  'PRODUTO_ACABADO': 'Produto Acabado',
  'RETALHO': 'Retalho',
  'EMBALAGEM': 'Embalagem',
};

const MOVEMENT_TYPE_LABELS: Record<string, string> = {
  'COMPRA': 'Compra',
  'PRODUCAO_ENTRADA': 'Produção (Entrada)',
  'DEVOLUCAO': 'Devolução',
  'CONSUMO': 'Consumo',
  'VENDA': 'Venda',
  'GERACAO_RETALHO': 'Geração de Retalho',
  'DESCARTE': 'Descarte',
  'AJUSTE': 'Ajuste',
};

const SECTION_CONFIG = {
  entradas: { label: 'Entradas', icon: FolderInput, description: 'Registrar novos itens e movimentações' },
  relatorios: { label: 'Relatórios', icon: BarChart3, description: 'Visualizar e exportar dados do estoque' },
  configuracoes: { label: 'Configurações', icon: Settings, description: 'Configurar grupos, status e preferências' },
};

export function StoragePage({ accessToken, initialSection = 'relatorios' }: StoragePageProps) {
  const toast = useToast();
  // Main section state - synced with header navigation
  const [activeSection, setActiveSection] = useState<StorageSection>(initialSection);

  // Sync with prop changes from header navigation
  useEffect(() => {
    setActiveSection(initialSection);
  }, [initialSection]);
  const [entradasView, setEntradasView] = useState<StorageEntradasView>('novo-item');
  const [relatoriosView, setRelatoriosView] = useState<StorageRelatoriosView>('itens');
  const [configView, setConfigView] = useState<StorageConfigView>('grupos');

  // Data state
  const [items, setItems] = useState<StockItem[]>([]);
  const [scraps, setScraps] = useState<StockItem[]>([]);
  const [movements, setMovements] = useState<StockMovement[]>([]);
  const [groups, setGroups] = useState<MaterialGroup[]>([]);
  const [customStatuses, setCustomStatuses] = useState<CustomStatus[]>([]);

  // Loading state
  const [loading, setLoading] = useState(true);
  const [loadingScraps, setLoadingScraps] = useState(false);

  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [scrapSearchQuery, setScrapSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<ItemStatus | 'all'>('all');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [showLowStock, setShowLowStock] = useState(false);

  // Dialog state
  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [scrapDialogOpen, setScrapDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);

  // Handlers
  const handleItemClick = (item: StockItem) => {
    setSelectedItem(item);
    setDetailDialogOpen(true);
  };

  const handleOpenMovementDialog = (item?: StockItem) => {
    if (item) setSelectedItem(item);
    setMovementDialogOpen(true);
  };

  const handleGenerateScrap = (item?: StockItem) => {
    if (item) {
      setSelectedItem(item);
      setDetailDialogOpen(false);
    }
    setScrapDialogOpen(true);
  };

  const handleNavigateToItem = (item: StockItem) => {
    setSelectedItem(item);
    setDetailDialogOpen(true);
  };

  const handleItemSuccess = () => {
    loadItems();
    loadMovements();
    loadScraps();
  };

  // Data fetching
  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchStockItems(accessToken, {
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        groupId: groupFilter !== 'all' ? groupFilter : undefined,
        belowMinStock: showLowStock || undefined,
      });
      let filtered = response.items || [];
      // Client-side type filter
      if (typeFilter !== 'all') {
        filtered = filtered.filter(item => item.itemType === typeFilter);
      }
      setItems(filtered);
    } catch (error) {
      toast.error((error as Error).message);
    } finally {
      setLoading(false);
    }
  }, [accessToken, searchQuery, statusFilter, groupFilter, typeFilter, showLowStock, toast]);

  const loadScraps = useCallback(async () => {
    try {
      setLoadingScraps(true);
      const response = await fetchStockItems(accessToken, {
        search: scrapSearchQuery || undefined,
      });
      const scrapItems = (response.items || []).filter(item => item.itemType === 'RETALHO');
      setScraps(scrapItems);
    } catch (error) {
      console.error('Error loading scraps:', error);
    } finally {
      setLoadingScraps(false);
    }
  }, [accessToken, scrapSearchQuery]);

  const loadMovements = useCallback(async () => {
    try {
      const response = await fetchStockMovements(accessToken);
      setMovements(response.items || []);
    } catch (error) {
      console.error('Error loading movements:', error);
    }
  }, [accessToken]);

  const loadGroups = useCallback(async () => {
    try {
      const data = await fetchMaterialGroups(accessToken);
      setGroups(data);
    } catch (error) {
      console.error('Error loading groups:', error);
    }
  }, [accessToken]);

  const loadCustomStatuses = useCallback(async () => {
    try {
      const data = await fetchCustomStatuses(accessToken);
      setCustomStatuses(data);
    } catch (error) {
      console.error('Error loading custom statuses:', error);
    }
  }, [accessToken]);

  // Initial data load
  useEffect(() => {
    loadItems();
    loadMovements();
    loadGroups();
    loadCustomStatuses();
    loadScraps();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeSection === 'relatorios') {
        if (relatoriosView === 'itens') loadItems();
        if (relatoriosView === 'retalhos') loadScraps();
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, scrapSearchQuery, statusFilter, groupFilter, typeFilter, showLowStock]);

  // CSV Export functions
  const exportItemsToCSV = () => {
    const headers = ['SKU', 'Nome', 'Tipo', 'Largura', 'Altura', 'Espessura', 'Quantidade', 'Unidade', 'Status', 'Localização', 'Grupo'];
    const rows = items.map(item => [
      item.skuCode,
      item.name,
      ITEM_TYPE_LABELS[item.itemType] || item.itemType,
      item.width || '',
      item.height || '',
      item.thickness || '',
      item.quantity,
      item.unit || 'un',
      STATUS_LABELS[item.status],
      item.location || '',
      item.groupName || '',
    ]);
    downloadCSV([headers, ...rows], 'estoque-itens.csv');
  };

  const exportMovementsToCSV = () => {
    const headers = ['Data', 'SKU', 'Item', 'Tipo', 'Quantidade', 'Motivo'];
    const rows = movements.map(m => [
      new Date(m.createdAt).toLocaleString('pt-BR'),
      m.itemSkuCode,
      m.itemName,
      MOVEMENT_TYPE_LABELS[m.movementType] || m.movementType,
      m.quantity,
      m.reason || '',
    ]);
    downloadCSV([headers, ...rows], 'estoque-movimentacoes.csv');
  };

  const exportScrapsToCSV = () => {
    const headers = ['SKU', 'Nome', 'Largura', 'Altura', 'Espessura', 'Quantidade', 'Status', 'Localização'];
    const rows = scraps.map(s => [
      s.skuCode,
      s.name,
      s.width || '',
      s.height || '',
      s.thickness || '',
      s.quantity,
      STATUS_LABELS[s.status],
      s.location || '',
    ]);
    downloadCSV([headers, ...rows], 'estoque-retalhos.csv');
  };

  const downloadCSV = (data: (string | number)[][], filename: string) => {
    const csv = data.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Stats
  const lowStockCount = items.filter(item => item.belowMinStock).length;
  const availableCount = items.filter(i => i.status === 'DISPONIVEL').length;

  const SectionIcon = SECTION_CONFIG[activeSection].icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <SectionIcon className="h-8 w-8 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              {SECTION_CONFIG[activeSection].label}
            </h1>
            <p className="text-muted-foreground mt-1">
              {SECTION_CONFIG[activeSection].description}
            </p>
          </div>
        </div>

        {/* Low stock badge */}
        {lowStockCount > 0 && (
          <Badge variant="destructive" className="gap-1">
            <AlertTriangle className="h-3 w-3" />
            {lowStockCount} abaixo do mínimo
          </Badge>
        )}
      </div>

      {/* ==================== ENTRADAS SECTION ==================== */}
      {activeSection === 'entradas' && (
        <div className="space-y-6">
          <Tabs value={entradasView} onValueChange={(v) => setEntradasView(v as StorageEntradasView)}>
            <TabsList>
              <TabsTrigger value="novo-item" className="gap-2">
                <Plus className="h-4 w-4" />
                Novo Item
              </TabsTrigger>
              <TabsTrigger value="movimentacao" className="gap-2">
                <ArrowUpDown className="h-4 w-4" />
                Movimentação
              </TabsTrigger>
              <TabsTrigger value="gerar-retalho" className="gap-2">
                <Scissors className="h-4 w-4" />
                Gerar Retalho
              </TabsTrigger>
            </TabsList>

            <TabsContent value="novo-item" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Cadastrar Novo Item</CardTitle>
                  <CardDescription>Adicione um novo material, ferramenta ou produto ao estoque</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Package className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">
                      Clique no botão abaixo para abrir o formulário completo de cadastro
                    </p>
                    <Button onClick={() => setCreateItemOpen(true)} size="lg">
                      <Plus className="h-5 w-5 mr-2" />
                      Novo Item de Estoque
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="movimentacao" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Registrar Movimentação</CardTitle>
                  <CardDescription>Entrada, saída ou ajuste de estoque</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <ArrowUpDown className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">
                      Registre compras, vendas, consumos ou ajustes de inventário
                    </p>
                    <Button onClick={() => handleOpenMovementDialog()} size="lg">
                      <ArrowUpDown className="h-5 w-5 mr-2" />
                      Nova Movimentação
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="gerar-retalho" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Gerar Retalho</CardTitle>
                  <CardDescription>Crie um retalho a partir de uma matéria-prima cortada</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <Scissors className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">
                      Selecione primeiro um item de Matéria-prima na lista de itens, ou clique abaixo para selecionar
                    </p>
                    <Button onClick={() => {
                      setActiveSection('relatorios');
                      setRelatoriosView('itens');
                      setTypeFilter('MATERIA_PRIMA');
                    }} size="lg" variant="outline">
                      <Search className="h-5 w-5 mr-2" />
                      Buscar Matéria-prima
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* ==================== RELATORIOS SECTION ==================== */}
      {activeSection === 'relatorios' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total de Itens</CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{items.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Disponíveis</CardTitle>
                <div className="h-3 w-3 rounded-full bg-green-500" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{availableCount}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Retalhos</CardTitle>
                <Scissors className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{scraps.length}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Estoque Baixo</CardTitle>
                <AlertTriangle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">{lowStockCount}</div>
              </CardContent>
            </Card>
          </div>

          <Tabs value={relatoriosView} onValueChange={(v) => setRelatoriosView(v as StorageRelatoriosView)}>
            <TabsList>
              <TabsTrigger value="itens">Lista de Itens</TabsTrigger>
              <TabsTrigger value="retalhos" className="gap-1">
                <Scissors className="h-3 w-3" />
                Retalhos
              </TabsTrigger>
              <TabsTrigger value="movimentacoes">Movimentações</TabsTrigger>
            </TabsList>

            {/* Items List */}
            <TabsContent value="itens" className="space-y-4 mt-4">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[200px] max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nome, SKU..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={groupFilter} onValueChange={setGroupFilter}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Grupo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os grupos</SelectItem>
                    {groups.map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os tipos</SelectItem>
                    <SelectItem value="MATERIA_PRIMA">Matéria-prima</SelectItem>
                    <SelectItem value="FERRAMENTA">Ferramenta</SelectItem>
                    <SelectItem value="PRODUTO_ACABADO">Produto Acabado</SelectItem>
                    <SelectItem value="RETALHO">Retalho</SelectItem>
                    <SelectItem value="EMBALAGEM">Embalagem</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as ItemStatus | 'all')}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os status</SelectItem>
                    <SelectItem value="DISPONIVEL">Disponível</SelectItem>
                    <SelectItem value="RESERVADO">Reservado</SelectItem>
                    <SelectItem value="CONSUMIDO">Consumido</SelectItem>
                    <SelectItem value="DESCARTE">Descarte</SelectItem>
                  </SelectContent>
                </Select>

                <Button
                  variant={showLowStock ? "default" : "outline"}
                  onClick={() => setShowLowStock(!showLowStock)}
                  size="sm"
                  className="gap-1"
                >
                  <AlertTriangle className="h-4 w-4" />
                  Estoque baixo
                </Button>

                <div className="flex-1" />

                <Button variant="outline" size="sm" onClick={exportItemsToCSV}>
                  <FileDown className="h-4 w-4 mr-2" />
                  Exportar CSV
                </Button>
              </div>

              {/* Items Table */}
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU / Nome</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Grupo</TableHead>
                      <TableHead>Dimensões</TableHead>
                      <TableHead className="text-right">Quantidade</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Localização</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">Carregando...</TableCell>
                      </TableRow>
                    ) : items.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                          Nenhum item encontrado
                        </TableCell>
                      </TableRow>
                    ) : (
                      items.map((item) => (
                        <TableRow
                          key={item.id}
                          onClick={() => handleItemClick(item)}
                          className={cn(
                            "cursor-pointer hover:bg-muted/50",
                            item.belowMinStock && "bg-red-50 dark:bg-red-950/20"
                          )}
                        >
                          <TableCell>
                            <div>
                              <div className="font-mono text-xs text-muted-foreground">{item.skuCode}</div>
                              <div className="font-medium">{item.name}</div>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{ITEM_TYPE_LABELS[item.itemType] || item.itemType}</Badge>
                          </TableCell>
                          <TableCell>
                            {item.groupName ? (
                              <Badge variant="secondary">{item.groupName}</Badge>
                            ) : (
                              <span className="text-muted-foreground text-sm">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {item.width && item.height ? (
                              <span className="text-sm">
                                {item.width} x {item.height}
                                {item.thickness && ` x ${item.thickness}`} mm
                              </span>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className={cn(item.belowMinStock && "text-destructive font-medium")}>
                              {item.quantity} {item.unit || 'un'}
                            </div>
                            {item.minStock && (
                              <div className="text-xs text-muted-foreground">Min: {item.minStock}</div>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[item.status]}>{STATUS_LABELS[item.status]}</Badge>
                          </TableCell>
                          <TableCell>
                            {item.location ? (
                              <div className="flex items-center gap-1 text-sm">
                                <MapPin className="h-3 w-3" />
                                {item.location}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Card>
            </TabsContent>

            {/* Scraps List */}
            <TabsContent value="retalhos" className="space-y-4 mt-4">
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar retalhos..."
                    value={scrapSearchQuery}
                    onChange={(e) => setScrapSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex-1" />
                <Button variant="outline" size="sm" onClick={exportScrapsToCSV}>
                  <FileDown className="h-4 w-4 mr-2" />
                  Exportar CSV
                </Button>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {loadingScraps ? (
                  <p className="text-muted-foreground col-span-full text-center py-8">Carregando...</p>
                ) : scraps.length === 0 ? (
                  <p className="text-muted-foreground col-span-full text-center py-8">
                    Nenhum retalho encontrado.
                  </p>
                ) : (
                  scraps.map((scrap) => (
                    <Card
                      key={scrap.id}
                      className="cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleItemClick(scrap)}
                    >
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="font-mono text-xs text-muted-foreground">{scrap.skuCode}</div>
                            <div className="font-medium">{scrap.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {scrap.width} x {scrap.height}
                              {scrap.thickness && ` x ${scrap.thickness}`} mm
                            </div>
                          </div>
                          <Badge variant={scrap.status === 'DISPONIVEL' ? 'default' : 'secondary'}>
                            {STATUS_LABELS[scrap.status]}
                          </Badge>
                        </div>
                        <div className="mt-3 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">
                            Qtd: <span className="font-medium text-foreground">{scrap.quantity}</span>
                          </span>
                          {scrap.location && (
                            <span className="flex items-center gap-1 text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              {scrap.location}
                            </span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </TabsContent>

            {/* Movements List */}
            <TabsContent value="movimentacoes" className="space-y-4 mt-4">
              <div className="flex items-center justify-end">
                <Button variant="outline" size="sm" onClick={exportMovementsToCSV}>
                  <FileDown className="h-4 w-4 mr-2" />
                  Exportar CSV
                </Button>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Histórico de Movimentações</CardTitle>
                  <CardDescription>Entradas, saídas e ajustes registrados</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Data</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                        <TableHead>Motivo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movements.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                            Nenhuma movimentação encontrada
                          </TableCell>
                        </TableRow>
                      ) : (
                        movements.map((movement) => (
                          <TableRow key={movement.id}>
                            <TableCell>
                              {new Date(movement.createdAt).toLocaleDateString('pt-BR', {
                                day: '2-digit', month: '2-digit', year: '2-digit',
                                hour: '2-digit', minute: '2-digit',
                              })}
                            </TableCell>
                            <TableCell>
                              <div>
                                <div className="font-mono text-xs text-muted-foreground">{movement.itemSkuCode}</div>
                                <div>{movement.itemName}</div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {MOVEMENT_TYPE_LABELS[movement.movementType] || movement.movementType}
                              </Badge>
                            </TableCell>
                            <TableCell className={cn(
                              "text-right font-medium",
                              movement.quantity > 0 ? "text-green-600" : "text-red-600"
                            )}>
                              {movement.quantity > 0 ? '+' : ''}{movement.quantity}
                            </TableCell>
                            <TableCell className="text-sm text-muted-foreground">
                              {movement.reason || '-'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* ==================== CONFIGURACOES SECTION ==================== */}
      {activeSection === 'configuracoes' && (
        <div className="space-y-6">
          <Tabs value={configView} onValueChange={(v) => setConfigView(v as StorageConfigView)}>
            <TabsList>
              <TabsTrigger value="grupos">Grupos de Materiais</TabsTrigger>
              <TabsTrigger value="status-customizados">Status Customizados</TabsTrigger>
              <TabsTrigger value="geral">Configurações Gerais</TabsTrigger>
            </TabsList>

            {/* Groups */}
            <TabsContent value="grupos" className="mt-6">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <div>
                    <CardTitle>Grupos de Materiais</CardTitle>
                    <CardDescription>Organize seus materiais em grupos para facilitar a busca e filtros</CardDescription>
                  </div>
                  <Button onClick={() => setCreateGroupOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Novo Grupo
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {groups.length === 0 ? (
                      <p className="text-muted-foreground col-span-full text-center py-8">
                        Nenhum grupo encontrado. Crie um grupo para organizar seus materiais.
                      </p>
                    ) : (
                      groups.map((group) => (
                        <Card key={group.id} className="hover:bg-muted/50">
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                              <div className="font-medium">{group.name}</div>
                              <Badge variant="secondary">{group.itemCount} item(ns)</Badge>
                            </div>
                          </CardContent>
                        </Card>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Custom Statuses */}
            <TabsContent value="status-customizados" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Status Customizados</CardTitle>
                  <CardDescription>Crie status adicionais além dos padrões (Disponível, Reservado, Consumido, Descarte)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-3">
                    {customStatuses.length === 0 ? (
                      <p className="text-muted-foreground">Nenhum status customizado criado</p>
                    ) : (
                      customStatuses.map((status) => (
                        <Badge
                          key={status.id}
                          style={{ backgroundColor: status.color }}
                          className="text-white px-3 py-1"
                        >
                          {status.name}
                        </Badge>
                      ))
                    )}
                    <Button variant="outline" size="sm">
                      <Plus className="h-3 w-3 mr-1" />
                      Novo Status
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* General Settings */}
            <TabsContent value="geral" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>Configurações Gerais</CardTitle>
                  <CardDescription>Preferências do sistema de estoque</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-center py-8">
                    Configurações gerais serão adicionadas em breve.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      )}

      {/* ==================== DIALOGS ==================== */}
      <CreateItemDialog
        open={createItemOpen}
        onOpenChange={setCreateItemOpen}
        accessToken={accessToken}
        groups={groups}
        onSuccess={handleItemSuccess}
      />

      <RecordMovementDialog
        open={movementDialogOpen}
        onOpenChange={setMovementDialogOpen}
        accessToken={accessToken}
        selectedItem={selectedItem}
        onSuccess={handleItemSuccess}
      />

      <ItemDetailDialog
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        accessToken={accessToken}
        item={selectedItem}
        onRecordMovement={handleOpenMovementDialog}
        onGenerateScrap={handleGenerateScrap}
        onNavigateToItem={handleNavigateToItem}
        onSuccess={handleItemSuccess}
      />

      <ScrapGenerationDialog
        open={scrapDialogOpen}
        onOpenChange={setScrapDialogOpen}
        accessToken={accessToken}
        parentItem={selectedItem}
        onSuccess={handleItemSuccess}
      />

      <CreateGroupDialog
        open={createGroupOpen}
        onOpenChange={setCreateGroupOpen}
        accessToken={accessToken}
        onSuccess={loadGroups}
      />
    </div>
  );
}
